import { TermsAcknowlegdementCard } from "@/components/footer";
import { i18nComponents } from "@/components/i18n";
import { RestrictedJurisdictionsBanner } from "@/components/legal.banners";
import { BannerCard, BannerTitle } from "@/components/ui/banners";
import { seo } from "@/lib/seo";
import { Link } from "@bidbattles/bloks/components/ui/link";
import {
	faBan,
	faBuilding,
	faCalendarDays,
	faCheckCircle,
	faChevronDown,
	faComments,
	faEnvelope,
	faGlobe,
	faHeadset,
	faInfoCircle,
	faLightbulb,
	faListUl,
	faPhone,
	faScaleBalanced,
	faShieldHalved,
	faTimesCircle,
	faTriangleExclamation,
	faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({
	component: RouteComponent,
	head: () => ({
		meta: [
			...seo({
				title: "Terms and Conditions | BidBattles",
			}),
		],
	}),
});

const tableOfContents = [
	{
		number: 1,
		label: "Acceptance of Terms",
		heading: "Acceptance of Terms",
		id: "acceptance-of-terms",
	},
	{
		number: 2,
		label: "Definitions",
		heading: "Definitions",
		id: "definitions",
	},
	{
		number: 3,
		label: "Eligibility",
		heading: "Eligibility Requirements",
		id: "eligibility",
	},
	{
		number: 4,
		label: "Account Registration",
		heading: "Account Registration and Security",
		id: "account-registration",
	},
	{
		number: 5,
		label: "Auction Mechanics",
		heading: "Auction Mechanics and Battle System",
		id: "auction-mechanics",
	},
	{
		number: 6,
		label: "Bidding Rules",
		heading: "Bidding Rules and Guidelines",
		id: "bidding-rules",
	},
	{
		number: 7,
		label: "Winning Conditions",
		heading: "Winning Conditions and Prize Fulfillment",
		id: "winning-conditions",
	},
	{
		number: 8,
		label: "Payment Terms",
		heading: "Payment Terms and Billing",
		id: "payment-terms",
	},
	{
		number: 9,
		label: "Refund Policy",
		heading: "Refund Policy",
		id: "refund-policy",
	},
	{
		number: 10,
		label: "Product Delivery",
		heading: "Product Delivery and Shipping",
		id: "product-delivery",
	},
	{
		number: 11,
		label: "Product Warranties",
		heading: "Product Warranties and Guarantees",
		id: "product-warranties",
	},
	{
		number: 12,
		label: "Prohibited Conduct",
		heading: "Prohibited Conduct and User Obligations",
		id: "prohibited-conduct",
	},
	{
		number: 13,
		label: "Intellectual Property",
		heading: "Intellectual Property Rights",
		id: "intellectual-property",
	},
	{
		number: 14,
		label: "Limitation of Liability",
		heading: "Limitation of Liability and Disclaimers",
		id: "limitation-of-liability",
	},
	{
		number: 15,
		label: "Dispute Resolution",
		heading: "Dispute Resolution",
		id: "dispute-resolution",
	},
	{
		number: 16,
		label: "Governing Law",
		heading: "Governing Law and Compliance",
		id: "governing-law",
	},
	{
		number: 17,
		label: "Changes to Terms",
		heading: "Modification of Terms",
		id: "changes-to-terms",
	},
	{
		number: 18,
		label: "Contact Information",
		heading: "Contact Information",
		id: "contact-information",
	},
] as const;

type SectionId = (typeof tableOfContents)[number]["id"];

const definitions = [
	{
		term: '"Battle"',
		description:
			"means a capped-player auction event where a limited number of participants compete to win a product by being the last bidder when the timer reaches zero.",
	},
	{
		term: '"Entry Price"',
		description:
			"means the mandatory fee (ranging from €30 to €100) required to join a Battle and secure a seat.",
	},
	{
		term: '"Bid" or "Bids"',
		description:
			"means the purchased credits used to place bids during a Battle. Three types exist: +0s Bid (€2.50), +3s Bid (€1.50), and +5s Bid (€0.50).",
	},
	{
		term: '"Bid Package"',
		description:
			"means a bundle of Bids purchased by Users, available in quantities of 5, 15, 30, or 60 Bids.",
	},
	{
		term: '"Open Seats"',
		description:
			"means the available participant slots in a Battle that have not yet been filled.",
	},
	{
		term: '"Capped-Player Auction"',
		description:
			"means our unique auction format where participation is limited to a maximum number of seats (e.g., 100, 200, or 1000 participants).",
	},
	{
		term: '"Battle Timer"',
		description:
			"means the countdown mechanism that extends by 0, 3, or 5 seconds (depending on bid type) each time a bid is placed.",
	},
	{
		term: '"Premium Battle"',
		description:
			"means high-value auction events featuring luxury vehicles or expensive products with entry prices of €100.",
	},
	{
		term: '"Live Battle"',
		description:
			"means an active auction event that has started and is currently accepting bids.",
	},
] as const;

function RouteComponent() {
	return (
		<main className="flex grow flex-col">
			<section className="px-box py-20 md:py-30">
				<PageContainer>
					<div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center">
						<h1 className="text-4xl leading-none font-bold text-gray-900 sm:text-5xl md:text-6xl">
							Terms and Conditions
						</h1>
						<p className="text-lg leading-7 text-gray-500 md:text-xl">
							Legal Terms of Service for BidBattles Platform
						</p>
						<div className="mt-1 flex items-center gap-3 rounded-lg border-l-4 border-accent-primary bg-gray-50 px-4 py-3 text-sm text-gray-900 sm:text-base">
							<FontAwesomeIcon aria-hidden icon={faCalendarDays} />
							<p>
								<i18nComponents.Strong>Last Updated:</i18nComponents.Strong>{" "}
								January 7, 2025
							</p>
						</div>
					</div>
				</PageContainer>
			</section>

			<section className="bg-gray-warm-100 px-box py-12 md:py-20">
				<PageContainer>
					<div className="mx-auto grid max-w-5xl items-start gap-8 xl:grid-cols-[14.5rem_minmax(0,47.5rem)]">
						<BannerCard className="text-gray-700 col-span-full">
							<BannerTitle>
								<FontAwesomeIcon
									aria-hidden
									icon={faTriangleExclamation}
									className="text-accent-primary"
								/>
								Important Legal Notice
							</BannerTitle>
							<p>
								Please read these Terms and Conditions carefully before using
								BidBattles. By accessing or using our platform, you agree to be
								bound by these terms. If you do not agree with any part of these
								terms, you must not use our services.
							</p>
						</BannerCard>
						<div className="xl:hidden">
							<MobileTableOfContents />
						</div>
						<aside className="sticky top-8 hidden xl:block">
							<TableOfContents />
						</aside>
						<div className="flex min-w-0 flex-col gap-8">
							{tableOfContents.map((section) => (
								<TermsSection key={section.id} {...section}>
									<SectionContent id={section.id} />
								</TermsSection>
							))}
							<TermsAcknowlegdementCard className="mx-auto" />
						</div>
					</div>
				</PageContainer>
			</section>
		</main>
	);
}

function PageContainer({ children }: React.PropsWithChildren) {
	return <div className="mx-auto w-full max-w-container">{children}</div>;
}

function TableOfContents() {
	return (
		<nav
			aria-label="Terms and Conditions table of contents"
			className="rounded-2xl bg-white p-6"
		>
			<div className="mb-6 flex items-start gap-3">
				<FontAwesomeIcon
					aria-hidden
					icon={faListUl}
					className="mt-1 text-xl text-accent-primary"
				/>
				<h2 className="text-xl leading-7 font-bold text-gray-900">
					Table of Contents
				</h2>
			</div>
			<ContentsLinks />
		</nav>
	);
}

function MobileTableOfContents() {
	return (
		<details className="group rounded-2xl bg-white p-4 sm:p-6">
			<summary className="flex cursor-pointer list-none items-center gap-3 text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gray-400 [&::-webkit-details-marker]:hidden">
				<FontAwesomeIcon
					aria-hidden
					icon={faListUl}
					className="text-xl text-accent-primary"
				/>
				<span className="text-xl font-bold">Table of Contents</span>
				<FontAwesomeIcon
					aria-hidden
					icon={faChevronDown}
					className="ms-auto transition-transform group-open:rotate-180"
				/>
			</summary>
			<div className="pt-6">
				<ContentsLinks />
			</div>
		</details>
	);
}

function ContentsLinks() {
	return (
		<ol className="flex flex-col gap-3 text-sm leading-5 text-gray-500">
			{tableOfContents.map((section) => (
				<li key={section.id}>
					<Link
						to="."
						hash={section.id}
						className="block rounded-sm hover:text-accent-primary focus-visible:text-accent-primary"
					>
						{section.number}. {section.label}
					</Link>
				</li>
			))}
		</ol>
	);
}

function SectionContent({ id }: { id: SectionId }) {
	switch (id) {
		case "acceptance-of-terms":
			return (
				<>
					<p>
						These Terms and Conditions (&quot;Terms&quot;, &quot;Terms and
						Conditions&quot;) constitute a legally binding agreement between you
						(&quot;User&quot;, &quot;you&quot;, &quot;your&quot;) and BidBattles
						B.V. (&quot;BidBattles&quot;, &quot;we&quot;, &quot;us&quot;,
						&quot;our&quot;) regarding your access to and use of the BidBattles
						platform, website, and related services (collectively, the
						&quot;Service&quot;).
					</p>
					<p>
						By accessing, browsing, or using the BidBattles platform, you
						acknowledge that you have read, understood, and agree to be bound by
						these Terms and Conditions, as well as our Privacy Policy. If you do
						not agree to these Terms, you are not authorized to access or use
						our Service.
					</p>
					<BannerCard className="text-gray-700">
						<BannerTitle className="text-gray-900">
							<FontAwesomeIcon
								aria-hidden
								icon={faInfoCircle}
								className="text-accent-primary"
							/>
							By Continuing, You Agree To:
						</BannerTitle>
						<LegalList
							items={[
								"Comply with all applicable laws and regulations",
								"Follow our auction rules and bidding procedures",
								"Accept our payment and refund policies",
								"Acknowledge the risks associated with auction participation",
							]}
						/>
					</BannerCard>
				</>
			);
		case "definitions":
			return (
				<>
					<p>For the purposes of these Terms and Conditions:</p>
					<dl className="flex flex-col gap-4 rounded-lg bg-gray-50 p-4 sm:p-6">
						{definitions.map((definition) => (
							<div key={definition.term}>
								<dt className="inline">
									<i18nComponents.Strong primary>
										{definition.term}
									</i18nComponents.Strong>
								</dt>{" "}
								<dd className="inline">{definition.description}</dd>
							</div>
						))}
					</dl>
				</>
			);
		case "eligibility":
			return (
				<>
					<p>
						To use the BidBattles platform, you must meet the following
						eligibility criteria:
					</p>
					<LegalSubheading>3.1 Age Requirement</LegalSubheading>
					<p>
						You must be at least 18 years of age to create an account and
						participate in Battles. By using our Service, you represent and
						warrant that you are 18 years or older.
					</p>
					<LegalSubheading>3.2 Geographic Restrictions</LegalSubheading>
					<p>
						BidBattles services are exclusively available to residents of the
						Netherlands and Belgium. You must have a valid shipping address in
						one of these countries to participate and receive products.
					</p>
					<LegalSubheading>3.3 Legal Capacity</LegalSubheading>
					<p>
						You must have the legal capacity to enter into binding contracts
						under applicable law. If you are registering on behalf of a legal
						entity, you represent that you have the authority to bind that
						entity to these Terms.
					</p>
					<LegalSubheading>3.4 Account Limitations</LegalSubheading>
					<p>
						Each individual may maintain only one (1) BidBattles account.
						Multiple accounts created by the same person are prohibited and may
						result in account suspension or termination without refund.
					</p>
					<RestrictedJurisdictionsBanner />
				</>
			);
		case "account-registration":
			return (
				<>
					<LegalSubheading>4.1 Account Creation</LegalSubheading>
					<p>
						To participate in Battles, you must create an account by providing
						accurate, complete, and current information including your full
						name, email address, date of birth, and shipping address. You agree
						to keep this information up to date at all times.
					</p>
					<LegalSubheading>4.2 Account Security</LegalSubheading>
					<p>
						You are responsible for maintaining the confidentiality of your
						account credentials, including your password. You agree to:
					</p>
					<LegalList
						items={[
							"Create a strong, unique password",
							"Not share your account credentials with any third party",
							"Notify BidBattles immediately of any unauthorized access or security breach",
							"Accept responsibility for all activities that occur under your account",
						]}
					/>
					<LegalSubheading>4.3 Account Verification</LegalSubheading>
					<p>
						BidBattles reserves the right to verify your identity and
						eligibility at any time. This may include requesting
						government-issued identification, proof of address, or other
						documentation. Failure to provide requested verification may result
						in account suspension or termination.
					</p>
					<LegalSubheading>4.4 Account Termination</LegalSubheading>
					<p>
						BidBattles reserves the right to suspend or terminate your account
						at any time, with or without notice, for any reason, including but
						not limited to:
					</p>
					<LegalList
						items={[
							"Violation of these Terms and Conditions",
							"Fraudulent or suspicious activity",
							"Multiple account creation",
							"Use of automated bidding tools or bots",
							"Failure to provide requested verification",
						]}
					/>
				</>
			);
		case "auction-mechanics":
			return <AuctionMechanicsContent />;
		case "bidding-rules":
			return <BiddingRulesContent />;
		case "winning-conditions":
			return <WinningConditionsContent />;
		case "payment-terms":
			return <PaymentTermsContent />;
		case "refund-policy":
			return <RefundPolicyContent />;
		case "product-delivery":
			return <ProductDeliveryContent />;
		case "product-warranties":
			return <ProductWarrantiesContent />;
		case "prohibited-conduct":
			return <ProhibitedConductContent />;
		case "intellectual-property":
			return <IntellectualPropertyContent />;
		case "limitation-of-liability":
			return <LimitationOfLiabilityContent />;
		case "dispute-resolution":
			return <DisputeResolutionContent />;
		case "governing-law":
			return <GoverningLawContent />;
		case "changes-to-terms":
			return <ChangesToTermsContent />;
		case "contact-information":
			return <ContactInformationContent />;
		default:
			return null;
	}
}

function AuctionMechanicsContent() {
	return (
		<>
			<LegalSubheading>5.1 Capped-Player Auction Format</LegalSubheading>
			<p>
				BidBattles operates a unique capped-player auction system where each
				Battle has a limited number of available seats. Once all seats are
				filled, the Battle becomes &quot;Full&quot; and no additional
				participants can join. Typical seat limits are:
			</p>
			<LabeledList
				items={[
					{ label: "Standard Battles:", description: "10 to 50 seats" },
					{
						label: "Premium Battles:",
						description: "51 to 200 seats",
					},
				]}
			/>
			<LegalSubheading>5.2 Battle Stages</LegalSubheading>
			<div className="flex flex-col gap-4 rounded-lg bg-gray-50 p-4 sm:p-6">
				<p>
					<i18nComponents.Strong>Open:</i18nComponents.Strong> Battle is
					accepting new participants until the maximum seats are filled. Users
					can pay the Entry Price to secure a seat.
				</p>
				<p>
					<i18nComponents.Strong>Live:</i18nComponents.Strong> Battle has
					started. Timer is active and participants can place bids to extend the
					countdown.
				</p>
				<p>
					<i18nComponents.Strong>Completed:</i18nComponents.Strong> Timer has
					reached zero and a winner has been determined.
				</p>
			</div>
			<LegalSubheading>5.3 Battle Timer Mechanism</LegalSubheading>
			<p>
				Each Battle features a countdown timer. When a participant places a bid,
				the timer extends based on the bid type used:
			</p>
			<LabeledList
				items={[
					{
						label: "+0s Bid:",
						description: "Timer does NOT extend (instant bid, high impact)",
					},
					{
						label: "+3s Bid:",
						description: "Timer extends by 3 seconds",
					},
					{
						label: "+5s Bid:",
						description: "Timer extends by 5 seconds (maximum time extension)",
					},
				]}
			/>
			<p>
				The Battle continues until the timer reaches zero without any new bids
				being placed. The last person to place a bid when the timer expires is
				declared the winner.
			</p>
			<LegalSubheading>5.4 Entry Price</LegalSubheading>
			<p>
				To join a Battle, you must pay a non-refundable Entry Price (once the
				Battle goes Live). Entry Prices vary by product:
			</p>
			<LegalList
				items={[
					"Electronics & Home Entertainment: €30 - €60",
					"Premium Vehicles (Tesla, Polestar, Kia): €100",
				]}
			/>
			<p>
				The Entry Price secures your seat and grants you eligibility to bid and
				potentially win the product.
			</p>
			<BannerCard className="text-gray-700">
				<BannerTitle className="text-gray-900">
					<FontAwesomeIcon
						aria-hidden
						icon={faLightbulb}
						className="text-accent-primary"
					/>
					Important: Entry Price vs. Bids
				</BannerTitle>
				<p>
					The Entry Price is separate from Bids. After paying the Entry Price to
					join a Battle, you must purchase Bid Packages separately to place bids
					during the Live Battle phase.
				</p>
			</BannerCard>
		</>
	);
}

function BiddingRulesContent() {
	const bidTypes = [
		{
			label: "+0s Bid - €30.00",
			description:
				"Instant power play. Timer does NOT extend. Use strategically in final moments.",
		},
		{
			label: "+3s Bid - €10.00",
			description:
				"Balanced option. Adds 3 seconds to timer. Most popular choice.",
		},
		{
			label: "+5s Bid - €6.00",
			description:
				"5 second time extension. Best value for prolonging Premium Battles.",
		},
	] as const;

	return (
		<>
			<LegalSubheading>6.1 Bid Types and Pricing</LegalSubheading>
			<p>
				BidBattles offers three distinct bid types, each with different
				strategic advantages:
			</p>
			<div className="flex flex-col gap-4 rounded-lg bg-gray-50 p-4 sm:p-6">
				{bidTypes.map((bid) => (
					<div
						key={bid.label}
						className="border-l-4 border-accent-primary ps-4"
					>
						<p className="text-gray-900">
							<i18nComponents.Strong primary>{bid.label}</i18nComponents.Strong>
						</p>
						<p className="text-sm leading-5">{bid.description}</p>
					</div>
				))}
			</div>
			<LegalSubheading>6.2 Bid Package Purchases</LegalSubheading>
			<p>
				Bids are available in packages or in single bids. Choose your own
				strategy.
			</p>
			{/*"Available packages include:"
			<LabeledList
				items={[
					{ label: "Starter (5 Bids):", description: "€12.50" },
					{ label: "Bronze (15 Bids):", description: "€25.00" },
					{
						label: "Silver (30 Bids):",
						description: "€45.00 (Most Popular)",
					},
					{ label: "Gold (60 Bids):", description: "€80.00" },
				]}
			/>*/}
			<p>
				Each package contains a mix of various bid types. Purchased bids are
				added to your battle of choice and only expire with a free account.
			</p>
			<LegalSubheading>6.3 Bidding Process</LegalSubheading>
			<p>
				Once a Battle is Live, you may place bids by selecting your desired bid
				type and confirming the action. Each bid:
			</p>
			<LegalList
				items={[
					"Is immediately deducted from your bid balance",
					"Cannot be canceled or refunded once placed",
					"Extends the Battle Timer according to bid type",
					'Makes you the current "Last Bidder"',
				]}
			/>
			<LegalSubheading>6.4 Fair Play Requirements</LegalSubheading>
			<p>
				Users must engage in fair play at all times. The following actions are
				strictly prohibited:
			</p>
			<LegalList
				items={[
					"Use of automated bidding scripts, bots, or third-party software",
					"Collusion with other participants to manipulate auction outcomes",
					"Creating multiple accounts to gain unfair advantages",
					"Attempting to exploit technical vulnerabilities or bugs",
					"Using VPNs or proxies to circumvent geographic restrictions",
				]}
			/>
			<BannerCard className="text-gray-700">
				<BannerTitle className="text-danger">
					<FontAwesomeIcon aria-hidden icon={faTriangleExclamation} />
					Zero Tolerance Policy
				</BannerTitle>
				<p>
					Violation of fair play rules will result in immediate account
					termination, forfeiture of all Entry Prices and purchased Bids, and
					potential legal action. BidBattles employs sophisticated fraud
					detection systems to monitor bidding patterns.
				</p>
			</BannerCard>
			<LegalSubheading>6.5 Bid Non-Refundability</LegalSubheading>
			<p>
				All bid purchases are final and non-refundable. Once a bid is placed in
				a Battle, it is consumed regardless of whether you win the auction. Bids
				are considered &quot;virtual currency&quot; and have no cash value
				outside the BidBattles platform.
			</p>
		</>
	);
}

function WinningConditionsContent() {
	return (
		<>
			<LegalSubheading>7.1 Determination of Winner</LegalSubheading>
			<p>
				The winner of a Battle is the participant who placed the last valid bid
				before the Battle Timer reached zero. When the timer expires without any
				new bids being placed, the Battle is immediately concluded and the
				winner is declared.
			</p>
			<LegalSubheading>7.2 Winner Notification</LegalSubheading>
			<p>Winners are notified immediately via:</p>
			<LegalList
				items={[
					"On-screen confirmation message",
					"Email notification to registered email address",
					"Account dashboard update showing won items",
				]}
			/>
			<LegalSubheading>7.3 Prize Claim Period</LegalSubheading>
			<p>
				Winners must claim their prize within 30 days of winning. To claim a
				prize, winners must:
			</p>
			<LegalList
				items={[
					"Verify their shipping address",
					"Provide any required identification or documentation",
					"Accept the product warranty and terms",
				]}
			/>
			<p>
				Failure to claim a prize within the specified period may result in
				forfeiture of the prize, with no refund of Entry Prices or Bids used.
			</p>
			<LegalSubheading>7.4 Total Investment Disclosure</LegalSubheading>
			<p>The total cost to win a product consists of:</p>
			<div className="flex flex-col gap-3 rounded-lg bg-gray-200 p-4 sm:p-6">
				<p className="text-gray-900">
					<i18nComponents.Strong>
						Total Investment = Entry Price + Cost of Bids Used
					</i18nComponents.Strong>
				</p>
				<p className="text-sm leading-5 text-gray-900">
					<i18nComponents.Strong>Example:</i18nComponents.Strong> If you paid
					€100 Entry Price for a Tesla Model S Battle and used €45 worth of bids
					from a Silver Package, your total investment is €145. While this is
					significantly below the retail price of €94,990, there is no guarantee
					of winning.
				</p>
			</div>
			<LegalSubheading>7.5 Prize Substitution</LegalSubheading>
			<p>
				BidBattles reserves the right to substitute prizes of equal or greater
				value if the advertised product becomes unavailable due to supply chain
				issues, manufacturer discontinuation, or other circumstances beyond our
				control. Winners will be notified of any substitutions and given the
				option to accept the substitute or receive a full refund of Entry Price.
			</p>
			<BannerCard className="text-gray-700">
				<BannerTitle className="text-gray-900">
					<FontAwesomeIcon
						aria-hidden
						icon={faTrophy}
						className="text-accent-primary"
					/>
					No Purchase Necessary Disclaimer
				</BannerTitle>
				<p>
					BidBattles is NOT a traditional lottery or gambling service. Winning
					requires skill, strategy, and timing. Participation involves financial
					risk, and there is no guarantee of winning or recovering your
					investment.
				</p>
			</BannerCard>
		</>
	);
}

function PaymentTermsContent() {
	return (
		<>
			<LegalSubheading>8.1 Accepted Payment Methods</LegalSubheading>
			<p>BidBattles accepts the following payment methods:</p>
			<LegalList
				items={[
					"Credit Cards (Visa, Mastercard, American Express)",
					"Debit Cards",
					"iDEAL / WERO (Netherlands)",
					"Bancontact (Belgium)",
					"PayPal",
				]}
			/>
			<LegalSubheading>8.2 Pricing and Currency</LegalSubheading>
			<p>
				All prices on the BidBattles platform are displayed in Euros (€) and
				include Value Added Tax (VAT) at 21%, as required by Dutch and Belgian
				law. The price you see is the final price you pay.
			</p>
			<LegalSubheading>8.3 Entry Price Payment</LegalSubheading>
			<p>
				Entry Prices must be paid in full at the time of joining a Battle. Entry
				Prices range from:
			</p>
			<LegalList
				items={[
					"€1 - €50 for standard battles",
					"€51 - €250 for premium battles",
				]}
			/>
			<p>
				Payment authorization must be successful before your seat is secured in
				the Battle.
			</p>
			{/*<LegalSubheading>8.4 Bid Package Payment</LegalSubheading>
			<p>Bid Packages are sold at the following prices (VAT included):</p>
			<div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-4 sm:p-6">
				<KeyValueRow label="Starter Package (5 Bids):" value="€12.50" />
				<KeyValueRow label="Bronze Package (15 Bids):" value="€25.00" />
				<KeyValueRow label="Silver Package (30 Bids):" value="€45.00" />
				<KeyValueRow label="Gold Package (60 Bids):" value="€80.00" />
			</div>*/}
			<LegalSubheading>8.5 Payment Processing</LegalSubheading>
			<p>
				BidBattles uses third-party payment processors to handle transactions
				securely. By making a payment, you agree to the terms and privacy
				policies of these payment processors. BidBattles is not responsible for
				errors or issues caused by third-party payment systems.
			</p>
			<LegalSubheading>8.6 Failed Payments</LegalSubheading>
			<p>
				If a payment fails for any reason (insufficient funds, expired card,
				declined transaction), your purchase will not be completed. You will not
				gain access to the Battle or receive the purchased Bids until payment is
				successfully processed.
			</p>
			<LegalSubheading>8.7 Invoicing and Receipts</LegalSubheading>
			<p>
				A digital invoice/receipt is automatically sent to your registered email
				address after each successful payment. This invoice includes:
			</p>
			<LegalList
				items={[
					"Transaction date and time",
					"Itemized purchase details",
					"Subtotal, VAT amount (21%), and total",
					"BidBattles B.V. company information and VAT number",
				]}
			/>
			<BannerCard className="text-gray-700">
				<BannerTitle className="text-gray-900">
					<FontAwesomeIcon
						aria-hidden
						icon={faShieldHalved}
						className="text-accent-primary"
					/>
					Secure Payment Guarantee
				</BannerTitle>
				<p>
					All transactions on BidBattles are encrypted using industry-standard
					SSL/TLS technology. We never store your full credit card details on
					our servers. All credit card details are handled by our payments
					partner, Mollie.
				</p>
			</BannerCard>
		</>
	);
}

function RefundPolicyContent() {
	return (
		<>
			<LegalSubheading>9.1 Entry Price Refunds</LegalSubheading>
			<p>Entry Price refund eligibility depends on Battle status:</p>
			<div className="flex flex-col gap-4">
				<div className="rounded-r-lg border-l-4 border-gray-300 bg-gray-50 p-4">
					<p className="flex items-center gap-2 text-gray-900">
						<FontAwesomeIcon aria-hidden icon={faCheckCircle} />
						<i18nComponents.Strong>
							REFUNDABLE - Battle Not Yet Live
						</i18nComponents.Strong>
					</p>
					<p className="mt-2 text-sm leading-5">
						If you join a Battle that has not yet started (still in
						&quot;Open&quot; status) and request cancellation before the Battle
						goes Live, you may receive a full refund of your Entry Price within
						21-30 business days.
					</p>
				</div>
				<div className="rounded-r-lg border-l-4 border-accent-primary bg-gray-50 p-4">
					<p className="flex items-center gap-2 text-gray-900">
						<FontAwesomeIcon
							aria-hidden
							icon={faTimesCircle}
							className="text-accent-primary"
						/>
						<i18nComponents.Strong>
							NON-REFUNDABLE - Battle Live or Completed
						</i18nComponents.Strong>
					</p>
					<p className="mt-2 text-sm leading-5">
						Once a Battle transitions to &quot;Live&quot; status and bidding
						begins, Entry Prices are non-refundable under any circumstances.
						This includes situations where you do not win the auction.
					</p>
				</div>
			</div>
			<LegalSubheading>9.2 Bid Package Refunds</LegalSubheading>
			<p>
				Bid Packages are considered virtual currency and are{" "}
				<i18nComponents.Strong>non-refundable</i18nComponents.Strong> once
				purchased. This policy applies regardless of whether the bids have been
				used or remain in your account. With a premium account after a finished
				battle your Bids are converted into BLC (BidBattle Loyalty Coins). BLC
				never expires and can be used to pay for bids in any future Battle.
			</p>
			<LegalSubheading>9.3 Used Bid Refunds</LegalSubheading>
			<p>
				Bids that have been placed during a Battle are immediately consumed and
				are <i18nComponents.Strong>non-refundable</i18nComponents.Strong> under
				any circumstances, including:
			</p>
			<LegalList
				items={[
					"Losing the Battle to another participant",
					"Disconnection or technical issues during the Battle",
					"Accidental bid placement",
					"Change of mind or strategy",
				]}
			/>
			<LegalSubheading>9.4 Product Return and Refund (Winners)</LegalSubheading>
			<p>
				Winners who receive physical products have the following rights under
				European consumer protection law:
			</p>
			<div className="flex flex-col gap-3 rounded-lg bg-gray-200 p-4 sm:p-6">
				<p className="text-gray-900">
					<i18nComponents.Strong>
						14-Day Cooling-Off Period
					</i18nComponents.Strong>
				</p>
				<p className="text-sm leading-5 text-gray-900">
					You have 14 calendar days from the date of product receipt to return
					the item for a BLC refund equivalent to the product's worth. The
					product must be in original condition, unopened (where applicable),
					and include all original packaging and accessories.
				</p>
				<p className="text-sm leading-5 text-gray-900">
					<i18nComponents.Strong>Important:</i18nComponents.Strong> Return
					shipping costs are the responsibility of the buyer unless the product
					is defective or damaged.
				</p>
			</div>
			<LegalSubheading>9.5 Defective Product Refunds</LegalSubheading>
			<p>
				If you receive a defective, damaged, or incorrect product, you are
				entitled to a full refund or replacement at no additional cost. Contact
				our support team within 7 days of delivery with photographic evidence of
				the defect.
			</p>
			<LegalSubheading>9.6 Battle Cancellation by BidBattles</LegalSubheading>
			<p>
				If BidBattles cancels a Battle due to technical issues, product
				unavailability, or other administrative reasons, all participants will
				receive a full refund of their Entry Price. Any bids used in that Battle
				will be returned to user accounts.
			</p>
			<LegalSubheading>9.7 Refund Processing Time</LegalSubheading>
			<p>
				Approved refunds are processed within{" "}
				<i18nComponents.Strong>5-10 business days</i18nComponents.Strong> and
				will be credited to the original payment method used for the purchase.
			</p>
			<BannerCard className="text-gray-700">
				<BannerTitle className="text-gray-900">
					<FontAwesomeIcon
						aria-hidden
						icon={faInfoCircle}
						className="text-accent-primary"
					/>
					Understanding the Risk
				</BannerTitle>
				<p>
					Participation in BidBattles involves financial risk. Entry Prices and
					Bids are spent with no guarantee of winning. Please participate
					responsibly and within your means.
				</p>
			</BannerCard>
		</>
	);
}

function ProductDeliveryContent() {
	return (
		<>
			<LegalSubheading>10.1 Shipping Regions</LegalSubheading>
			<p>
				Shipping is exclusively available within the Netherlands and Belgium. We
				cannot ship products to any other countries or territories.
			</p>
			<TranslationNotice
				original="In Dutch/Flemish: Verzending alleen mogelijk binnen Nederland en België"
				translation="Shipping only possible within the Netherlands and Belgium"
			/>
			<LegalSubheading>10.2 Shipping Costs</LegalSubheading>
			<p>
				Shipping costs vary depending on the product size, weight, and
				destination country. These costs are calculated and displayed before
				final checkout when claiming your won item.
			</p>
			<div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-4 sm:p-6">
				<p className="text-gray-900">
					<i18nComponents.Strong>Typical Shipping Fees:</i18nComponents.Strong>
				</p>
				<p>
					<i18nComponents.Strong>Small packages:</i18nComponents.Strong> Less
					than 1kg (€5 - €15)
				</p>
				<p>
					<i18nComponents.Strong>Large packages:</i18nComponents.Strong> More
					than 1kg (€15 - €350)
				</p>
				<p>
					Shipping costs are calculated within the entry price of every Battle
					<i18nComponents.Strong>Vehicles:</i18nComponents.Strong> Free delivery
					within NL/BE (arranged separately)
				</p>
				<p className="text-sm leading-5 text-gray-500 italic">
					In Dutch: De verzendkosten kunnen per land verschillen (Shipping costs
					may vary by country)
				</p>
			</div>
			<LegalSubheading>10.3 Delivery Timeframes</LegalSubheading>
			<p>
				Products are typically shipped within 3-5 business days after the winner
				claims the prize and confirms their shipping details. Estimated delivery
				times:
			</p>
			<LabeledList
				items={[
					{ label: "Netherlands:", description: "2-5 business days" },
					{ label: "Belgium:", description: "3-7 business days" },
					{
						label: "Premium Vehicles:",
						description:
							"2-4 weeks (coordinated delivery with specialized transport)",
					},
				]}
			/>
			<LegalSubheading>10.4 Tracking Information</LegalSubheading>
			<p>
				All shipments include tracking numbers, which are sent to your
				registered email address once the product is dispatched. You can track
				your delivery status in real-time through the shipping carrier&apos;s
				website.
			</p>
			<LegalSubheading>10.5 Delivery Acceptance</LegalSubheading>
			<p>
				Upon delivery, you must inspect the package for any visible damage. If
				the package appears damaged, you should:
			</p>
			<LegalList
				items={[
					"Note the damage on the delivery receipt",
					"Take photographs of the package and its contents",
					"Contact BidBattles support within 24 hours",
				]}
			/>
			<LegalSubheading>10.6 Failed Delivery Attempts</LegalSubheading>
			<p>
				If the shipping carrier is unable to deliver the product after multiple
				attempts, the package will be returned to BidBattles. You will be
				contacted to arrange redelivery. Additional shipping fees may apply for
				reshipment.
			</p>
			<LegalSubheading>10.7 Incorrect Shipping Address</LegalSubheading>
			<p>
				You are responsible for providing an accurate and complete shipping
				address. BidBattles is not liable for delivery failures or delays caused
				by incorrect address information. Reshipping to a corrected address will
				incur additional fees.
			</p>
		</>
	);
}

function ProductWarrantiesContent() {
	return (
		<>
			<LegalSubheading>11.1 Legal Warranty Conditions</LegalSubheading>
			<p>
				All products offered on BidBattles are covered by legal warranty
				conditions as required by Dutch and Belgian consumer protection law.
			</p>
			<TranslationNotice
				original="In Dutch: Op alle producten zijn de wettelijke garantievoorwaarden van toepassing"
				translation="Translation: Legal warranty conditions apply to all products"
			/>
			<LegalSubheading>11.2 Manufacturer Warranty</LegalSubheading>
			<p>
				All products are brand new (unless explicitly stated otherwise) and
				include the manufacturer&apos;s original warranty. Warranty periods vary
				by product and manufacturer:
			</p>
			<LabeledList
				items={[
					{
						label: "Electronics:",
						description: "Typically 1-2 years manufacturer warranty",
					},
					{
						label: "Apple Products:",
						description: "1 year limited warranty + optional AppleCare",
					},
					{
						label: "Vehicles:",
						description:
							"Manufacturer warranty as per original terms (typically 3-8 years)",
					},
				]}
			/>
			<LegalSubheading>11.3 Retail Price Guarantee</LegalSubheading>
			<p>
				All products are advertised with the manufacturer&apos;s suggested
				retail price (MSRP) or the price listed by our authorized suppliers.
			</p>
			<div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-4 text-sm leading-5">
				<p className="italic">
					In Dutch: De genoemde prijs is de consumentenadviesprijs van onze
					aanbieder
				</p>
				<p className="text-gray-500">
					Translation: The stated price is the consumer recommended price from
					our supplier
				</p>
			</div>
			<LegalSubheading>11.4 Product Authenticity</LegalSubheading>
			<p>
				BidBattles guarantees that all products are authentic and sourced
				directly from authorized distributors or manufacturers. We do not sell
				counterfeit, refurbished (unless stated), or grey market products.
			</p>
			<LegalSubheading>11.5 Defective Products</LegalSubheading>
			<p>If you receive a defective product:</p>
			<LegalList
				items={[
					"Contact BidBattles support within 7 days of receipt",
					"Provide photographic/video evidence of the defect",
					"BidBattles will arrange for replacement or full refund (including Entry Price and shipping)",
				]}
			/>
			<LegalSubheading>11.6 Warranty Claims</LegalSubheading>
			<p>
				For warranty claims after the initial 14-day return period, you must
				contact the manufacturer directly using the warranty documentation
				included with your product. BidBattles can provide assistance in
				facilitating warranty claims but is not responsible for manufacturer
				warranty service.
			</p>
		</>
	);
}

function ProhibitedConductContent() {
	return (
		<>
			<p>
				When using BidBattles, you agree to comply with all applicable laws and
				regulations. The following actions are strictly prohibited and may
				result in immediate account termination:
			</p>
			<LegalSubheading>12.1 Fraud and Manipulation</LegalSubheading>
			<LegalList
				items={[
					"Using automated bidding tools, bots, scripts, or macros",
					"Creating multiple accounts to gain unfair advantages",
					"Colluding with other users to manipulate Battle outcomes",
					"Sharing account credentials with others",
					"Attempting to reverse engineer or exploit platform vulnerabilities",
				]}
			/>
			<LegalSubheading>12.2 Payment Fraud</LegalSubheading>
			<LegalList
				items={[
					"Using stolen credit cards or payment methods",
					"Initiating chargebacks for legitimate transactions",
					"Providing false payment information",
					"Attempting to exploit payment processing errors",
				]}
			/>
			<LegalSubheading>12.3 Misrepresentation</LegalSubheading>
			<LegalList
				items={[
					"Providing false identity information during registration",
					"Using VPNs or proxies to circumvent geographic restrictions",
					"Claiming to be a resident of Netherlands/Belgium when you are not",
					"Registering accounts for persons under 18 years of age",
				]}
			/>
			<LegalSubheading>12.4 Abuse and Harassment</LegalSubheading>
			<LegalList
				items={[
					"Harassing other users or BidBattles staff",
					"Using threatening, abusive, or offensive language",
					"Spamming support channels with frivolous requests",
					"Attempting to damage BidBattles' reputation through false claims",
				]}
			/>
			<LegalSubheading>12.5 Technical Interference</LegalSubheading>
			<LegalList
				items={[
					"Attempting to gain unauthorized access to BidBattles systems",
					"Distributing malware or viruses through the platform",
					"Performing denial-of-service attacks or similar disruptions",
					"Scraping or harvesting data from the platform",
				]}
			/>
			<BannerCard className="text-gray-700">
				<BannerTitle className="text-gray-900">
					<FontAwesomeIcon
						aria-hidden
						icon={faBan}
						className="text-accent-primary"
					/>
					Consequences of Prohibited Conduct
				</BannerTitle>
				<ul className="flex flex-col gap-2 text-sm leading-5">
					{[
						"Immediate account suspension or termination",
						"Forfeiture of all Entry Prices and purchased Bids",
						"Banning from all future participation",
						"Legal action and criminal prosecution where applicable",
						"Reporting to law enforcement and fraud authorities",
					].map((item) => (
						<li key={item}>
							<i18nComponents.Strong>{item}</i18nComponents.Strong>
						</li>
					))}
				</ul>
			</BannerCard>
		</>
	);
}

function IntellectualPropertyContent() {
	return (
		<>
			<LegalSubheading>13.1 BidBattles Trademarks and Content</LegalSubheading>
			<p>
				All content on the BidBattles platform, including but not limited to
				text, graphics, logos, images, software, audio, video, and data
				compilations, is the property of BidBattles B.V. or its content
				suppliers and is protected by international copyright, trademark, and
				intellectual property laws.
			</p>
			<p>
				The BidBattles name, logo, and all related product and service names are
				trademarks of BidBattles B.V. You may not use these trademarks without
				our prior written permission.
			</p>
			<LegalSubheading>13.2 Limited License to Use</LegalSubheading>
			<p>
				BidBattles grants you a limited, non-exclusive, non-transferable,
				revocable license to access and use the platform solely for your
				personal, non-commercial use. This license does not include:
			</p>
			<LegalList
				items={[
					"Reproduction, modification, or distribution of platform content",
					"Commercial use of any materials from the platform",
					"Decompilation, reverse engineering, or disassembly of software",
					"Creating derivative works based on BidBattles content",
				]}
			/>
			<LegalSubheading>13.3 User Content</LegalSubheading>
			<p>
				If you submit, post, or provide any content on BidBattles (such as
				profile information, reviews, or feedback), you grant BidBattles a
				worldwide, royalty-free, perpetual license to use, reproduce, modify,
				adapt, publish, and display such content for the purposes of operating
				and promoting the platform.
			</p>
			<LegalSubheading>13.4 Third-Party Trademarks</LegalSubheading>
			<p>
				Product names, brand names, and logos displayed on BidBattles are
				trademarks of their respective manufacturers and owners.
				BidBattles&apos; use of these trademarks is for identification purposes
				only and does not imply endorsement or affiliation.
			</p>
			<LegalSubheading>13.5 Copyright Infringement Claims</LegalSubheading>
			<p>
				If you believe that your copyrighted work has been used on BidBattles in
				a way that constitutes copyright infringement, please contact us with:
			</p>
			<LegalList
				items={[
					"Description of the copyrighted work",
					"Location of the infringing material on our platform",
					"Your contact information",
					"A statement of good faith belief",
					"Electronic or physical signature",
				]}
			/>
		</>
	);
}

function LimitationOfLiabilityContent() {
	return (
		<>
			<LegalSubheading>14.1 Platform Availability</LegalSubheading>
			<p>
				BidBattles strives to provide continuous, uninterrupted service, but we
				do not guarantee that the platform will be available at all times. The
				platform may be temporarily unavailable due to:
			</p>
			<LegalList
				items={[
					"Scheduled maintenance and upgrades",
					"Technical failures or server issues",
					"Internet connectivity problems",
					"Force majeure events",
				]}
			/>
			<LegalSubheading>14.2 No Guarantee of Winning</LegalSubheading>
			<p>
				BidBattles makes no guarantee that you will win any Battle or recover
				your investment. Participation involves financial risk, and outcomes are
				dependent on user skill, strategy, and competition from other
				participants.
			</p>
			<LegalSubheading>14.3 Technical Issues During Battles</LegalSubheading>
			<p>
				BidBattles is not liable for losses incurred due to technical issues
				during a Battle, including but not limited to:
			</p>
			<LegalList
				items={[
					"Internet connectivity issues on your end",
					"Browser compatibility problems",
					"Delayed bid placement due to network latency",
					"Device malfunctions",
				]}
			/>
			<p>
				We recommend using a stable internet connection and up-to-date browser
				when participating in Battles.
			</p>
			<LegalSubheading>14.4 Limitation of Damages</LegalSubheading>
			<p>
				To the maximum extent permitted by law, BidBattles B.V., its directors,
				employees, and affiliates shall not be liable for any indirect,
				incidental, special, consequential, or punitive damages, including but
				not limited to:
			</p>
			<LegalList
				items={[
					"Loss of profits or revenue",
					"Loss of data or information",
					"Loss of opportunity or business advantage",
					"Emotional distress or mental anguish",
				]}
			/>
			<p>
				In no event shall BidBattles&apos; total liability exceed the total
				amount you paid to BidBattles in the 12 months preceding the claim.
			</p>
			<LegalSubheading>14.5 Third-Party Links</LegalSubheading>
			<p>
				The BidBattles platform may contain links to third-party websites or
				services. We are not responsible for the content, privacy practices, or
				availability of third-party sites. Accessing third-party links is at
				your own risk.
			</p>
			<LegalSubheading>14.6 Force Majeure</LegalSubheading>
			<p>
				BidBattles shall not be liable for any failure to perform obligations
				due to circumstances beyond our reasonable control, including but not
				limited to natural disasters, wars, terrorism, labor disputes,
				government actions, or technical infrastructure failures.
			</p>
			<BannerCard className="text-gray-700">
				<BannerTitle className="text-gray-900">
					<FontAwesomeIcon
						aria-hidden
						icon={faTriangleExclamation}
						className="text-accent-primary"
					/>
					Important Legal Notice
				</BannerTitle>
				<p className="text-sm leading-5">
					These limitations of liability apply to the fullest extent permitted
					by applicable law. Some jurisdictions do not allow certain limitations
					on liability, so some limitations may not apply to you.
				</p>
			</BannerCard>
		</>
	);
}

function DisputeResolutionContent() {
	return (
		<>
			<LegalSubheading>15.1 Informal Resolution</LegalSubheading>
			<p>
				Before initiating any formal dispute resolution process, you agree to
				first contact BidBattles support to attempt to resolve the issue
				informally. Most disputes can be resolved quickly and satisfactorily
				through direct communication.
			</p>
			<p>
				Contact our support team at:{" "}
				<a
					href="mailto:support@bidbattles.nl"
					className="break-words text-gray-900 underline decoration-gray-300 underline-offset-4 hover:text-accent-primary focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gray-400"
				>
					<i18nComponents.Strong>support@bidbattles.nl</i18nComponents.Strong>
				</a>
			</p>
			<LegalSubheading>15.2 Mediation</LegalSubheading>
			<p>
				If informal resolution is unsuccessful, both parties agree to attempt
				mediation before pursuing litigation. Mediation will be conducted by a
				mutually agreed upon mediator in the Netherlands or Belgium, with costs
				shared equally between parties.
			</p>
			<LegalSubheading>15.3 Arbitration (Optional)</LegalSubheading>
			<p>
				For disputes exceeding €5,000, either party may request binding
				arbitration under the rules of the Netherlands Arbitration Institute
				(NAI). The arbitration will be conducted in Dutch or English, at the
				choice of the claimant.
			</p>
			<LegalSubheading>15.4 Jurisdiction and Legal Action</LegalSubheading>
			<p>
				If mediation and arbitration are unsuccessful or inappropriate, disputes
				shall be resolved in the competent courts of Amsterdam, the Netherlands.
				By using BidBattles, you consent to the exclusive jurisdiction of these
				courts.
			</p>
			<LegalSubheading>15.5 Class Action Waiver</LegalSubheading>
			<p>
				You agree to resolve disputes with BidBattles on an individual basis
				only. You waive the right to participate in class action lawsuits or
				class-wide arbitration against BidBattles.
			</p>
		</>
	);
}

function GoverningLawContent() {
	return (
		<>
			<LegalSubheading>16.1 Applicable Law</LegalSubheading>
			<p>
				These Terms and Conditions and your use of BidBattles shall be governed
				by and construed in accordance with the laws of the Netherlands, without
				regard to its conflict of law principles.
			</p>
			<LegalSubheading>16.2 Consumer Protection Laws</LegalSubheading>
			<p>
				BidBattles complies with all applicable Dutch and Belgian consumer
				protection regulations, including:
			</p>
			<LegalList
				items={[
					"EU Consumer Rights Directive",
					"Dutch Civil Code (Burgerlijk Wetboek)",
					"Belgian Consumer Protection Code",
					"General Data Protection Regulation (GDPR)",
				]}
			/>
			<LegalSubheading>16.3 Tax Obligations</LegalSubheading>
			<p>
				All prices include 21% Value Added Tax (VAT) as required by Dutch and
				Belgian law. BidBattles is registered with the Dutch Chamber of Commerce
				and holds a valid VAT identification number.
			</p>
			<p>
				Winners are responsible for reporting winnings to tax authorities if
				required by local law.
			</p>
			<LegalSubheading>16.4 Regulatory Compliance</LegalSubheading>
			<p>
				BidBattles operates in full compliance with Dutch gaming and auction
				regulations. Our business model is structured as a skill-based
				entertainment service, not gambling or lottery activity.
			</p>
		</>
	);
}

function ChangesToTermsContent() {
	return (
		<>
			<LegalSubheading>17.1 Right to Modify</LegalSubheading>
			<p>
				BidBattles reserves the right to modify, update, or replace these Terms
				and Conditions at any time, at our sole discretion. Changes may be made
				to:
			</p>
			<LegalList
				items={[
					"Reflect changes in our business practices",
					"Comply with new legal or regulatory requirements",
					"Improve user experience and clarity",
					"Address security concerns or technical changes",
				]}
			/>
			<LegalSubheading>17.2 Notification of Changes</LegalSubheading>
			<p>
				When we make material changes to these Terms, we will notify you by:
			</p>
			<LegalList
				items={[
					'Updating the "Last Updated" date at the top of this document',
					"Sending an email notification to your registered email address",
					"Displaying a prominent notice on the BidBattles platform",
				]}
			/>
			<LegalSubheading>17.3 Acceptance of Modified Terms</LegalSubheading>
			<p>
				Your continued use of BidBattles after any modifications to these Terms
				constitutes your acceptance of the updated Terms. If you do not agree
				with the modified Terms, you must immediately stop using the platform
				and may request account deletion.
			</p>
			<LegalSubheading>17.4 Review Responsibility</LegalSubheading>
			<p>
				It is your responsibility to review these Terms periodically to stay
				informed of any changes. We recommend checking this page before
				participating in new Battles or making purchases.
			</p>
		</>
	);
}

function ContactInformationContent() {
	const contactLinkClassName =
		"break-words underline decoration-gray-300 underline-offset-4 hover:text-accent-primary focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gray-400";

	return (
		<>
			<p>
				If you have any questions, concerns, or comments regarding these Terms
				and Conditions, please contact us through any of the following methods:
			</p>
			<div className="grid gap-6 md:grid-cols-2">
				<div className="min-w-0 rounded-xl bg-[rgba(255,123,60,0.10)] p-4 sm:p-6">
					<div className="mb-4 flex items-center gap-4">
						<span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-accent-primary text-xl text-white">
							<FontAwesomeIcon aria-hidden icon={faBuilding} />
						</span>
						<h3 className="text-xl leading-7 font-bold text-gray-900">
							Company Details
						</h3>
					</div>
					<dl className="flex flex-col gap-2 text-sm leading-5">
						<div className="flex flex-wrap gap-x-1">
							<dt className="font-bold">Legal Name:</dt>
							<dd>BidBattles B.V.</dd>
						</div>
						<div className="flex flex-wrap gap-x-1">
							<dt className="font-bold">KVK Number:</dt>
							<dd>94769974</dd>
						</div>
						<div className="flex flex-wrap gap-x-1">
							<dt className="font-bold">VAT Number:</dt>
							<dd className="break-all">NL866887970B01</dd>
						</div>
					</dl>
				</div>
				<div className="min-w-0 rounded-xl bg-[rgba(255,123,60,0.10)] p-4 sm:p-6">
					<div className="mb-4 flex items-center gap-4">
						<span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-accent-primary text-xl text-white">
							<FontAwesomeIcon aria-hidden icon={faHeadset} />
						</span>
						<h3 className="text-xl leading-7 font-bold text-gray-900">
							Support Channels
						</h3>
					</div>
					<ul className="flex flex-col gap-3 text-sm leading-5">
						<li className="flex min-w-0 gap-3">
							<FontAwesomeIcon
								aria-hidden
								icon={faEnvelope}
								className="mt-1 shrink-0 text-accent-primary"
							/>
							<span className="min-w-0">
								<i18nComponents.Strong>Email:</i18nComponents.Strong>{" "}
								<a
									href="mailto:support@bidbattles.nl"
									className={contactLinkClassName}
								>
									support@bidbattles.nl
								</a>
							</span>
						</li>
						<li className="flex min-w-0 gap-3">
							<FontAwesomeIcon
								aria-hidden
								icon={faGlobe}
								className="mt-1 shrink-0 text-accent-primary"
							/>
							<span className="min-w-0 break-words">
								<i18nComponents.Strong>Website:</i18nComponents.Strong>{" "}
								www.bidbattles.nl
							</span>
						</li>
					</ul>
				</div>
			</div>
			<div className="rounded-lg bg-gray-50 p-4 sm:p-6">
				<h3 className="mb-3 font-bold text-gray-900">Business Hours</h3>
				<div className="grid gap-4 text-sm leading-5 sm:grid-cols-2">
					<div>
						<p className="font-bold">Customer Support:</p>
						<p>Monday - Friday: 9:00 AM - 6:00 PM CET</p>
						<p>Saturday: 10:00 AM - 4:00 PM CET</p>
						<p>Sunday: Closed</p>
					</div>
					<div>
						<p className="font-bold">Platform Availability:</p>
						<p>Battles run 24/7</p>
						<p>Support tickets answered within 24 hours</p>
						<p>Emergency issues handled immediately</p>
					</div>
				</div>
			</div>
			<BannerCard className="text-gray-700">
				<BannerTitle className="text-gray-900">
					<FontAwesomeIcon
						aria-hidden
						icon={faScaleBalanced}
						className="text-accent-primary"
					/>
					Legal Correspondence
				</BannerTitle>
				<p className="text-sm leading-5">
					For legal notices, copyright claims, or formal complaints, please send
					correspondence to our registered business address via certified mail
					or to:{" "}
					<a
						href="mailto:legal@bidbattles.nl"
						className="break-words text-gray-900 underline decoration-gray-300 underline-offset-4 hover:text-accent-primary focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gray-400"
					>
						<i18nComponents.Strong>legal@bidbattles.nl</i18nComponents.Strong>
					</a>
				</p>
			</BannerCard>
		</>
	);
}

function LegalSubheading({ children }: React.PropsWithChildren) {
	return (
		<h3 className="text-xl leading-7 font-bold text-gray-900">{children}</h3>
	);
}

function TranslationNotice({
	original,
	translation,
}: {
	original: string;
	translation: string;
}) {
	return (
		<BannerCard className="text-gray-700">
			<p className="text-gray-900">
				<i18nComponents.Strong>{original}</i18nComponents.Strong>
			</p>
			<p className="text-sm leading-5">{translation}</p>
		</BannerCard>
	);
}

function LabeledList({
	items,
}: {
	items: ReadonlyArray<{ label: string; description: string }>;
}) {
	return (
		<ul className="flex list-disc flex-col gap-2 ps-6 marker:text-gray-500">
			{items.map((item) => (
				<li key={item.label} className="ps-1">
					<i18nComponents.Strong>{item.label}</i18nComponents.Strong>{" "}
					{item.description}
				</li>
			))}
		</ul>
	);
}

function KeyValueRow({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-wrap justify-between gap-x-6 gap-y-1 text-gray-700">
			<i18nComponents.Strong>{label}</i18nComponents.Strong>
			<i18nComponents.Strong>{value}</i18nComponents.Strong>
		</div>
	);
}

function LegalList({ items }: { items: readonly string[] }) {
	return (
		<ul className="flex list-disc flex-col gap-2 ps-6 marker:text-gray-500">
			{items.map((item) => (
				<li key={item} className="ps-1">
					{item}
				</li>
			))}
		</ul>
	);
}

function TermsSection({
	number,
	heading,
	id,
	children,
}: React.PropsWithChildren<{
	number: number;
	heading: string;
	id: string;
}>) {
	const headingId = `${id}-heading`;

	return (
		<section
			id={id}
			aria-labelledby={headingId}
			className="scroll-mt-8 rounded-2xl bg-white p-4 sm:p-6 md:p-8"
		>
			<div className="flex items-start gap-4">
				<span
					aria-hidden
					className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-btn-bg-from to-btn-bg-to text-sm font-bold text-white md:size-12 md:text-base"
				>
					{number}
				</span>
				<h2
					id={headingId}
					className="min-w-0 pt-1 text-2xl leading-8 font-bold text-gray-900 md:text-3xl md:leading-9"
				>
					{heading}
				</h2>
			</div>
			{children && (
				<div className="mt-6 flex flex-col gap-4 text-base leading-[1.625] text-gray-700">
					{children}
				</div>
			)}
		</section>
	);
}
